import { getECPMessage } from "@/shared/auth/signAndRunNclayer";
import apiClient from "@/shared/api/apiClient";
import i18n from "@/shared/config/i18n";
import {
  getLocalizedMessage,
  getErrorCode,
} from "@/shared/lib/errors/getLocalizedMessage";

export default class NclayerAuthPort {
  constructor() {
    this.wsUrl = "wss://127.0.0.1:13579/";
    this.socket = null;
  }

  async connect() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return this.socket;
    }

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.wsUrl);

      ws.onopen = () => {
        this.socket = ws;
        resolve(ws);
      };

      ws.onerror = (err) => {
        console.error("Ошибка подключения к NCALayer:", err);
        this.socket = null;
        reject(new Error(i18n.global.t("NCALAYER_PORT_UNAVAILABLE")));
      };

      ws.onclose = () => {
        this.socket = null;
      };
    });
  }

  async sign(data) {
    const ws = await this.connect();

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("NCALayer: нет активного соединения через WebSocket");
      return { signatures: [] };
    }

    const locale = "ru"; // можно позже сделать динамическим
    ws.send(getECPMessage(data, locale));

    return new Promise((resolve, reject) => {
      const onMessage = ({ data }) => {
        try {
          if (!data.trim()) return;

          const resp = JSON.parse(data);

          if (resp?.status === true) {
            ws.removeEventListener("message", onMessage);

            if (
              Array.isArray(resp.body?.result) &&
              resp.body.result.length > 0
            ) {
              resolve({ signatures: resp.body.result });
            } else {
              console.warn(
                "[NCALayer] sign returned status=true but empty result, body:",
                resp.body
              );
              reject(new Error(i18n.global.t("Действие отменено")));
            }

            return;
          }

          if (resp?.status === false || resp?.error) {
            ws.removeEventListener("message", onMessage);
            console.error("[NCALayer] sign rejected, raw response:", resp);
            const reason =
              resp.error ||
              resp.code ||
              resp.message ||
              resp.body?.message ||
              resp.body?.error ||
              resp.body?.reason;
            reject(
              new Error(
                reason
                  ? `${i18n.global.t(
                      "Ошибка при подписании данных"
                    )}: ${reason}`
                  : i18n.global.t("Ошибка при подписании данных")
              )
            );
          }
        } catch (e) {
          ws.removeEventListener("message", onMessage);
          console.error(
            "[NCALayer] failed to parse sign response, raw data:",
            data,
            "error:",
            e
          );
          reject(e);
        }
      };

      ws.addEventListener("message", onMessage);

      ws.onerror = (err) => {
        ws.removeEventListener("message", onMessage);
        console.error("[NCALayer] WebSocket error during sign:", err);
        reject(new Error(i18n.global.t("NCALAYER_SIGN_ERROR")));
      };
    });
  }

  // 0) Получить challenge (nonce + expires_in) для подписи
  async getChallenge(action /* "login" | "register" */) {
    const response = await apiClient.post(
      "/login/challenge/",
      { action },
      { metaData: { isNotificationDisabled: true } }
    );
    const payload = response.data?.payload || response.data || {};
    const { nonce, expires_in } = payload;
    if (!nonce) throw new Error("CHALLENGE_INVALID");
    return { nonce, expires_in };
  }

  // 1) INIT (или REGISTRATION)
  async login(signedCms, mode = {}) {
    try {
      const endpoint =
        mode === "registration" ? "/login/registration/" : "/login/init/";

      const body = { cms: signedCms };

      const response = await apiClient.post(endpoint, body, {
        metaData: { isNotificationDisabled: true },
      });

      const { session_id, roles, date_before, date_after } =
        response.data.payload || {};

      if (!session_id) throw new Error("Не получен session_id");
      if (!roles || roles.length === 0)
        throw new Error(i18n.global.t("Нет доступных ролей"));
      if (!date_before) throw new Error("Не получен date_before");
      if (!date_after) throw new Error("Не получен date_after");
      if (date_before) localStorage.setItem("cert_date_before", date_before);
      if (date_after) localStorage.setItem("cert_date_after", date_after);

      return {
        _success: true,
        session_id,
        roles,
        date_before,
        date_after,
      };
    } catch (err) {
      return {
        _success: false,
        message: err.message,
      };
    }
  }

  // 2) CONFIRM ROLE — отдельная функция
  async confirm(session_id, role_code) {
    try {
      const response = await apiClient.post("/login/confirm/", {
        session_id,
        role_code,
      });

      const {
        access_token,
        refresh_token,
        access_token_expires_in,
        refresh_token_expires_in,
      } = response.data.payload;

      return {
        _success: true,
        access_token,
        refresh_token,
        access_token_expires_in,
        refresh_token_expires_in,
      };
    } catch (err) {
      console.error("Ошибка авторизации через NCALayer:", err);
      const detail =
        err?.response?.data?.detail ?? err?.response?.data?.payload?.detail;
      const backendMessage = getLocalizedMessage(detail);
      const code = getErrorCode(detail);
      return {
        _success: false,
        code,
        message:
          backendMessage ||
          err.message ||
          i18n.global.t("Ошибка при входе через NCALayer"),
      };
    }
  }

  async refreshTokens() {
    // Refresh-token живёт в HttpOnly cookie — браузер сам прикладывает её
    // к этому запросу (Path=/auth/login). Тело пустое.
    try {
      const response = await apiClient.post("/login/refresh/");

      const { access_token, access_token_expires_in } =
        response.data.payload || {};

      return {
        _success: !!access_token,
        access_token,
        access_token_expires_in,
      };
    } catch (err) {
      console.error("Ошибка обновления токенов в NclayerAuthPort:", err);
      return { _success: false, message: err.message };
    }
  }

  async serverLogout() {
    // Серверный logout: бэк инвалидирует session_id и отдаёт
    // Set-Cookie: refresh_token=; Max-Age=0 — браузер чистит cookie.
    try {
      await apiClient.post("/login/logout/");
      return { _success: true };
    } catch (err) {
      console.error("Ошибка серверного logout в NclayerAuthPort:", err);
      return { _success: false, message: err.message };
    }
  }

  async extendTokenLife() {
    try {
      await apiClient.get("/login/check-session/");

      return {
        success: true,
      };
    } catch (err) {
      console.error("Ошибка продления жизни токена в NclayerAuthPort:", err);
      return { _success: false, message: err.message };
    }
  }
}
